import {useI18n} from 'i18n/I18nContext'
import React, {ReactElement} from 'react'
import {DraggableProvided} from 'react-beautiful-dnd'
import styled from 'styled-components'
import {cloneSectionWithNewIds} from 'TemplateEditor/cloneSectionWithNewIds'
import {useEditor} from 'TemplateEditor/EditorProvider'
import {useTemplate} from 'TemplateEditor/TemplateProvider'
import {shouldShowForPurchasedTicketTier} from 'TemplateEditor/templateTicketVisibility'
import {SectionConfigType} from 'TemplateEditor/Layout/Sections/SectionConfig'
import {
  newSection1,
  PreviewSection1,
  Section1,
  section1TypeId,
} from 'TemplateEditor/Layout/Sections/Types/Section1'
import {
  newSection111,
  PreviewSection111,
  Section111,
  section111TypeId,
} from 'TemplateEditor/Layout/Sections/Types/Section111'
import {
  newSection1111,
  PreviewSection1111,
  Section1111,
  section1111TypeId,
} from 'TemplateEditor/Layout/Sections/Types/Section1111'
import {
  newSection112,
  PreviewSection112,
  Section112,
  section112TypeId,
} from 'TemplateEditor/Layout/Sections/Types/Section112'
import {
  newSection1by1,
  PreviewSection1by1,
  Section1by1,
  section1by1TypeId,
} from 'TemplateEditor/Layout/Sections/Types/Section1by1'
import {
  newSection1by2,
  PreviewSection1by2,
  Section1by2,
  section1by2TypeId,
} from 'TemplateEditor/Layout/Sections/Types/Section1by2'
import {
  newSection211,
  PreviewSection211,
  Section211,
  section211TypeId,
} from 'TemplateEditor/Layout/Sections/Types/Section211'
import {
  newSection2by1,
  PreviewSection2by1,
  Section2by1,
  section2by1TypeId,
} from 'TemplateEditor/Layout/Sections/Types/Section2by1'
import {Section as SectionData} from 'TemplateEditor/template'
import {confirmAlert} from 'react-confirm-alert'
import {removeByNestedKeys} from 'utils/lib/json'

export type SectionProps = {
  section: SectionData
  sectionIndex: number
  key: number
}

export type ListSectionType = {
  typeId: string
  component: (props: any) => ReactElement
  preview: () => ReactElement
  new: () => SectionData
}

export const ListSections: ListSectionType[] = [
  {
    typeId: section1TypeId,
    component: Section1,
    preview: PreviewSection1,
    new: newSection1,
  },
  {
    typeId: section1by1TypeId,
    component: Section1by1,
    preview: PreviewSection1by1,
    new: newSection1by1,
  },
  {
    typeId: section1by2TypeId,
    component: Section1by2,
    preview: PreviewSection1by2,
    new: newSection1by2,
  },
  {
    typeId: section2by1TypeId,
    component: Section2by1,
    preview: PreviewSection2by1,
    new: newSection2by1,
  },
  {
    typeId: section111TypeId,
    component: Section111,
    preview: PreviewSection111,
    new: newSection111,
  },
  {
    typeId: section112TypeId,
    component: Section112,
    preview: PreviewSection112,
    new: newSection112,
  },
  {
    typeId: section211TypeId,
    component: Section211,
    preview: PreviewSection211,
    new: newSection211,
  },
  {
    typeId: section1111TypeId,
    component: Section1111,
    preview: PreviewSection1111,
    new: newSection1111,
  },
]

export const defaultSectionConfig = (): SectionConfigType => {
  return {
    padding: {
      top: 0,
      bottom: 0,
      left: 0,
      right: 0,
    },
    border: {
      color: '#ffffff',
      width: 0,
      radius: 0,
    },
    background: '#ffffff',
    backgroundImage: null,
    revertMobile: false,
    visibility: 'all',
    visible: true,
    visible_ticket_ids: [],
  }
}

export function Section(props: {
  section: SectionData
  sectionIndex: number
  provided?: DraggableProvided
}) {
  const {section, sectionIndex, provided} = props
  const {t} = useI18n()
  const {template, updateTemplate} = useTemplate()
  const {
    editMode,
    editElement,
    setEditElement,
    viewerEventTicketId,
    clearSelectionAfterMutation,
  } = useEditor()

  const handleSetEditMode = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!editMode) return

    setEditElement(`sections.${sectionIndex}`)
  }

  const handleCopySection = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (!editMode) return

    const sections = template.sections?.length ? [...template.sections] : []
    const duplicate = cloneSectionWithNewIds(section)
    sections.splice(sectionIndex + 1, 0, duplicate)
    updateTemplate({...template, sections})
  }

  const handleRemoveSection = (e: React.MouseEvent) => {
    e.stopPropagation()
    e.preventDefault()
    if (!editMode) return

    const sectionKey = `sections.${sectionIndex}`
    confirmAlert({
      message: t('templateEditor.confirmRemoveSection'),
      closeOnClickOutside: false,
      buttons: [
        {
          label: t('common.yes'),
          onClick: () => {
            const next = removeByNestedKeys(template, sectionKey)
            updateTemplate(next)
            if (
              editElement === sectionKey ||
              (editElement?.startsWith(`${sectionKey}.`) ?? false)
            ) {
              clearSelectionAfterMutation(next)
            }
          },
        },
        {
          label: t('common.no'),
          onClick: () => {},
        },
      ],
    })
  }

  if (!editMode && !section.visible) return null

  if (
    !editMode &&
    !shouldShowForPurchasedTicketTier({
      editMode: false,
      visible: Boolean(section.visible),
      visibleTicketIds: section.visible_ticket_ids,
      viewerEventTicketId,
    })
  ) {
    return null
  }

  return (
    <SectionHolder
      {...provided?.draggableProps}
      {...provided?.dragHandleProps}
      ref={provided?.innerRef}
      $editMode={editMode}
      $section={section}
    >
      {editMode && (
        <SectionHoverActions>
          <CopyButton
            type="button"
            onClick={handleCopySection}
            title={t('templateEditor.copySection')}
            aria-label={t('templateEditor.copySection')}
          >
            <i className="fa-light fa-copy"></i>
          </CopyButton>
          <RemoveButton
            type="button"
            onClick={handleRemoveSection}
            title={t('templateEditor.removeSection')}
            aria-label={t('templateEditor.removeSection')}
          >
            <i className="fa-light fa-trash-can"></i>
          </RemoveButton>
          <EditButton
            type="button"
            onClick={handleSetEditMode}
            title={t('templateEditor.sidebarEdit')}
            aria-label={t('templateEditor.sidebarEdit')}
          >
            <i className="fa-light fa-edit"></i>
          </EditButton>
        </SectionHoverActions>
      )}

      {ListSections.map((listSection) => {
        if (section.type !== listSection.typeId) return null

        return listSection.component({
          sectionIndex: sectionIndex,
          section: section,
          key: sectionIndex,
        })
      })}
    </SectionHolder>
  )
}

const SectionHoverActions = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  z-index: 30;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0;
  pointer-events: auto;
  opacity: 1;
  transition: opacity 0.12s ease;
  border-radius: 0 0 0 8px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.14);
`

/** Matches sub-section / column edit affordance (ResponsiveSection EditButton). */
const actionBtn = styled.button`
  margin: 0;
  padding: 4px 12px;
  min-height: 34px;
  min-width: 38px;
  color: white;
  cursor: pointer;
  font-size: 18px;
  border: none;
  line-height: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
  opacity: 0.9;
  transition: opacity 0.15s ease;

  &:hover {
    opacity: 1;
  }

  & > i {
    line-height: 1;
    display: block;
  }
`

const CopyButton = styled(actionBtn)`
  background: #5c6bc0;
  border-bottom-left-radius: 4px;
`

const RemoveButton = styled(actionBtn)`
  background: #c62828;
`

const EditButton = styled(actionBtn)`
  background: #f7496d;
`

const SectionHolder = styled.div<{
  $editMode: boolean
  $section: SectionData
}>`
  position: relative;
  z-index: 0;

  border: ${(props) => (props.$editMode ? '1px grey dashed' : '')};

  ${(props) => (!props.$section.visible ? 'border: 4px #f7496d solid' : '')};

  ${(props) =>
    props.$section.visibility === 'desktop'
      ? '@container (max-width: 991px) { display: none;}'
      : ''};
  ${(props) =>
    props.$section.visibility === 'mobile'
      ? '@container (min-width: 991px) { display: none;}'
      : ''};

  /* Hide section bar while a block is hovered so block copy/remove stay clickable */
  &:has([data-template-block-root]:hover) ${SectionHoverActions} {
    opacity: 0;
    pointer-events: none;
    z-index: 0;
  }
`
